import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { Checkbox, FormControl, MenuItem, Modal, Select, TextField } from '@mui/material';

import { getPblVettingApplication, reviewPblLawyerApplication } from '@/api/pbl.api';
import PageContainer from '@/components/Container/PageContainer';
import ReviewSummaryContent from '@/components/Pbl/VolunteerRegistration/ReviewSummaryContent';
import { buildReviewSummaryFromProfile } from '@/components/Pbl/VolunteerRegistration/reviewSummary';
import { Button } from '@/components/UI/Button';
import Loading from '@/components/UI/Loading';
import { pblDecisionReasons, pblVettingStatusLabels } from '@/data/pbl';
import PATH from '@/router/path';
import type { PblLawyerProfile, PblVettingChecklist } from '@/types/pbl';
import { errorHandler, successHandler } from '@/utils/toastHandler';
import useResize from '@/hooks/useResize';
import {
  getPblVettingProfileCreatedAt,
  getPblVettingProfileReference,
  getPblVettingProfileStatus,
} from '@/pages/Pbl/VettingDashboard/vettingDashboardState';

import { pblVettingInfoModalScrollArea, pblVettingModalSurface } from './modalStyles';
import {
  buildPblVettingReviewPayload,
  createEmptyPblVettingChecklist,
  getPblVettingChecklistGroups,
  getPblVettingChecklistRounds,
  getPblVettingCompletedProBonoCaseCount,
  getPblVettingConductSupportingDocuments,
  getPblVettingFirmAuthorisationDocument,
  getPblVettingResubmitReasons,
  getPblVettingRoundDate,
  isPblVettingChecklistComplete,
  pblVettingChecklistErrorMessage,
  shouldShowPblVettingReviewActions,
  type PblVettingReviewAction,
} from './vettingDetailState';
import { useTranslation } from 'react-i18next';

type ReviewModalType = PblVettingReviewAction;

const TitleBar = styled.div<{ isMobile: boolean }>`
  display: flex;
  align-items: center;
  gap: 20px;
  width: 100%;
  min-width: 0;
  flex-direction: ${(p) => (p.isMobile ? 'column' : 'row')};
  font-size: ${(p) => (p.isMobile ? p.theme.fontSizes.md : p.theme.fontSizes.lg)};
  font-weight: 400;

  strong {
    font-weight: 700;
  }

  @media (max-width: 639px) {
    gap: 10px;
    margin-bottom: 16px;

    > span {
      width: 100%;
      min-width: 0;
      text-align: center;
      overflow-wrap: anywhere;
    }
  }
`;

const ReviewCard = styled.div`
  box-sizing: border-box;
  min-width: 0;
  width: min(100%, 875px);
  padding: 28px 46px;
  border-radius: ${(p) => p.theme.radii.lg};
  background: ${(p) => p.theme.colors.light};

  @media (max-width: 768px) {
    padding: 24px 16px;
  }

  @media (max-width: 639px) {
    padding: 20px 12px 24px;
    border-radius: 12px;
  }
`;

const LawyerSummaryCard = styled.div`
  box-sizing: border-box;
  min-width: 0;
  width: 100%;
  padding: 18px 18px 16px;
  border: 1px solid ${(p) => p.theme.colors.gray200};
  border-radius: ${(p) => p.theme.radii.lg};
`;

const LawyerName = styled.h2`
  margin: 0 0 14px;
  color: ${(p) => p.theme.colors.primary500};
  font-size: ${(p) => p.theme.fontSizes.md};
  font-weight: 700;
`;

const SummaryRows = styled.div`
  display: grid;
  grid-template-columns: minmax(260px, 42%) minmax(0, 1fr);
  gap: 10px 24px;

  @media (max-width: 768px) {
    grid-template-columns: 121px minmax(0, 1fr);
    gap: 8px 12px;
  }
`;

const Label = styled.div`
  color: ${(p) => p.theme.colors.gray300};
  line-height: 1.25;
`;

const Value = styled.div`
  color: ${(p) => p.theme.colors.gray500};
  line-height: 1.25;
  overflow-wrap: anywhere;
`;

const Divider = styled.hr`
  margin: 16px 0 12px;
  border: 0;
  border-top: 1px solid ${(p) => p.theme.colors.gray100};
`;

const SectionTitle = styled.div`
  margin: 28px 0 14px;
  color: ${(p) => p.theme.colors.primary500};
  font-weight: 700;
`;

const CvButton = styled.button`
  width: fit-content;
  min-width: 114px;
  max-width: 100%;
  min-height: 40px;
  padding: 7px 14px;
  border: 0;
  margin-right: 10px;
  border-radius: ${(p) => p.theme.radii.base};
  background: ${(p) => p.theme.colors.primary500};
  color: ${(p) => p.theme.colors.light};
  font-size: ${(p) => p.theme.fontSizes.base};
  line-height: 1.2;
  white-space: normal;
  cursor: pointer;

  @media (max-width: 639px) {
    margin-right: 0;
    font-size: 16px;
    overflow-wrap: anywhere;
  }
`;

const ResubmitTable = styled.table`
  width: 100%;
  border-collapse: collapse;

  th,
  td {
    padding: 12px 14px;
    border-bottom: 1px solid ${(p) => p.theme.colors.gray100};
    text-align: left;
  }

  th {
    color: ${(p) => p.theme.colors.gray300};
    font-weight: 400;
  }

  th:last-child,
  td:last-child {
    width: 130px;
    text-align: right;
  }

  @media (max-width: 639px) {
    display: block;

    thead {
      position: absolute;
      width: 1px;
      height: 1px;
      overflow: hidden;
      clip: rect(0 0 0 0);
      clip-path: inset(50%);
    }

    tbody,
    tr,
    td {
      display: block;
      width: 100%;
    }

    tr {
      box-sizing: border-box;
      margin-bottom: 10px;
      padding: 10px 12px;
      border-radius: 10px;
      background: ${(p) => p.theme.colors.gray000};
    }

    tr:nth-child(even) {
      background: ${(p) => p.theme.colors.light};
    }

    td,
    td:last-child {
      display: grid;
      grid-template-columns: 96px minmax(0, 1fr);
      gap: 10px;
      box-sizing: border-box;
      padding: 5px 0;
      border: 0;
      text-align: left;
      overflow-wrap: anywhere;

      &::before {
        content: attr(data-label);
        color: ${(p) => p.theme.colors.gray300};
      }
    }
  }
`;

const FormArea = styled.div`
  display: grid;
  gap: 28px;
  margin-top: 10px;
`;

const ActionRow = styled.div<{ isMobile: boolean }>`
  display: flex;
  flex-direction: ${(p) => (p.isMobile ? 'column' : 'row')};
  gap: 14px;
  margin-top: 36px;
`;

const ActionButton = styled.button<{ tone: 'approve' | 'pending' }>`
  min-width: ${(p) => (p.tone === 'approve' ? '215px' : '114px')};
  height: 48px;
  padding: 0 22px;
  border-radius: ${(p) => p.theme.radii.base};
  color: ${(p) => p.theme.colors.light};
  background: ${(p) => (p.tone === 'approve' ? p.theme.colors.secondary500 : p.theme.colors.warning)};
  font-size: ${(p) => p.theme.fontSizes.md};
  cursor: pointer;

  @media (max-width: 639px) {
    width: 100%;
    min-width: 0;
  }
`;

const ModalCard = styled.div<{ wide?: boolean }>`
  position: absolute;
  top: 50%;
  left: 50%;
  width: min(${(p) => (p.wide ? '930px' : '500px')}, calc(100vw - 40px));
  max-height: calc(100vh - 80px);
  overflow: ${(p) => (p.wide ? 'hidden' : 'auto')};
  transform: translate(-50%, -50%);
  padding: ${(p) => (p.wide ? '28px 50px' : '48px')};
  border-radius: ${pblVettingModalSurface.borderRadius};
  background-color: ${pblVettingModalSurface.backgroundColor};
  box-shadow: ${pblVettingModalSurface.boxShadow};
  outline: none;

  @media (max-width: 768px) {
    padding: ${(p) => (p.wide ? '28px 20px' : '34px 22px')};
  }

  @media (max-width: 639px) {
    width: calc(100vw - 24px);
    max-height: calc(100dvh - 24px);
    padding: ${(p) => (p.wide ? '24px 12px' : '30px 16px')};
  }
`;

const InfoModalScrollArea = styled.div`
  max-height: ${pblVettingInfoModalScrollArea.maxHeight};
  overflow-y: ${pblVettingInfoModalScrollArea.overflowY};
  padding-right: ${pblVettingInfoModalScrollArea.paddingRight};
  scrollbar-gutter: ${pblVettingInfoModalScrollArea.scrollbarGutter};
`;

const CloseButton = styled.button`
  position: absolute;
  top: 12px;
  right: 14px;
  width: 28px;
  height: 28px;
  color: ${(p) => p.theme.colors.gray500};
  font-size: 28px;
  line-height: 1;
  cursor: pointer;
`;

const ConfirmText = styled.div`
  margin: 20px 0 34px;
  color: ${(p) => p.theme.colors.gray500};
  font-size: ${(p) => p.theme.fontSizes.base};
  line-height: 1.4;
  text-align: center;
`;

const PendingReason = styled.div`
  margin-top: 24px;
  color: ${(p) => p.theme.colors.primary500};
  font-size: ${(p) => p.theme.fontSizes.base};
  line-height: 1.35;
  text-align: center;
`;

const ChecklistSection = styled.section`
  display: grid;
  gap: 18px;
  margin-top: 8px;
`;

const ChecklistGroup = styled.div`
  display: grid;
  gap: 8px;
`;

const ChecklistGroupTitle = styled.h3`
  margin: 0;
  color: ${(p) => p.theme.colors.primary500};
  font-size: ${(p) => p.theme.fontSizes.base};
  font-weight: 700;
`;

const ChecklistItem = styled.label`
  display: grid;
  grid-template-columns: 34px minmax(0, 1fr);
  align-items: flex-start;
  gap: 8px;
  color: ${(p) => p.theme.colors.gray500};
  line-height: 1.35;
  cursor: pointer;

  .MuiCheckbox-root {
    align-self: flex-start;
    padding: 0;
    color: ${(p) => p.theme.colors.primary500};
  }
`;

const ChecklistError = styled.div`
  color: ${(p) => p.theme.colors.danger};
  font-size: ${(p) => p.theme.fontSizes.sm};
  line-height: 1.35;
`;

const ChecklistHistorySection = styled.section`
  margin-top: 30px;
`;

const ChecklistHistoryRound = styled.div`
  display: grid;
  gap: 12px;
  padding: 12px 0;
  border-top: 1px solid ${(p) => p.theme.colors.gray100};
`;

const ChecklistHistoryTitle = styled.h3`
  margin: 0;
  color: ${(p) => p.theme.colors.gray500};
  font-size: ${(p) => p.theme.fontSizes.base};
  font-weight: 700;
`;

const ChecklistHistoryList = styled.ul`
  display: grid;
  gap: 8px;
  margin: 0;
  padding: 0;
  list-style: none;
`;

const ChecklistHistoryItem = styled.li`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 12px;
  color: ${(p) => p.theme.colors.gray500};
  line-height: 1.35;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 4px;
  }
`;

const ChecklistStatus = styled.span<{ completed: boolean }>`
  color: ${(p) => (p.completed ? p.theme.colors.secondary500 : p.theme.colors.danger)};
  font-weight: 700;
  white-space: nowrap;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: center;
  gap: 16px;

  @media (max-width: 576px) {
    flex-direction: column;
  }
`;

const ModalButton = styled.button<{ variant: 'primary' | 'outline' }>`
  min-width: 186px;
  height: 50px;
  border: 1px solid ${(p) => p.theme.colors.primary500};
  border-radius: ${(p) => p.theme.radii.base};
  background: ${(p) => (p.variant === 'primary' ? p.theme.colors.primary500 : p.theme.colors.light)};
  color: ${(p) => (p.variant === 'primary' ? p.theme.colors.light : p.theme.colors.primary500)};
  font-size: ${(p) => p.theme.fontSizes.md};
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }

  @media (max-width: 576px) {
    width: 100%;
    min-width: 0;
  }
`;

const StateMessage = styled.div`
  box-sizing: border-box;
  min-width: 0;
  width: min(100%, 875px);
  padding: 36px 24px;
  border-radius: ${(p) => p.theme.radii.lg};
  background: ${(p) => p.theme.colors.light};
  color: ${(p) => p.theme.colors.gray400};
  text-align: center;
`;

const getAdmittedYears = (application: PblLawyerProfile) => {
  if (!application.hk_solicitor_admission_year) return '-';
  const admittedYears = Math.max(0, new Date().getFullYear() - Number(application.hk_solicitor_admission_year));
  return `${admittedYears} years`;
};

const getChecklistItemValue = (checklist: PblVettingChecklist, groupKey: keyof PblVettingChecklist, itemKey: string) =>
  Boolean((checklist[groupKey] as Record<string, boolean>)[itemKey]);

const getChecklistHistoryTitle = (round: NonNullable<PblLawyerProfile['vetting_rounds']>[number]) => {
  const statusLabel =
    pblVettingStatusLabels[round.status as keyof typeof pblVettingStatusLabels] || String(round.status);
  const date = getPblVettingRoundDate(round);

  return `Round ${round.round_no} - ${statusLabel}${date ? ` - ${date}` : ''}`;
};

const VettingChecklistFields = ({
  checklist,
  error,
  onToggle,
}: {
  checklist: PblVettingChecklist;
  error: string;
  onToggle: (groupKey: keyof PblVettingChecklist, itemKey: string) => void;
}) => {
  //   @ts-ignore
  const { t } = useTranslation();
  const pblVettingChecklistGroups = useMemo(() => getPblVettingChecklistGroups(t), [t]);

  return (
    <ChecklistSection>
      {pblVettingChecklistGroups.map((group) => (
        <ChecklistGroup key={group.key}>
          <ChecklistGroupTitle>{group.title}</ChecklistGroupTitle>
          {group.items.map((item) => (
            <ChecklistItem key={item.key}>
              <Checkbox
                checked={getChecklistItemValue(checklist, group.key, item.key)}
                onChange={() => onToggle(group.key, item.key)}
                inputProps={{ 'aria-label': item.label }}
              />
              <span>{item.label}</span>
            </ChecklistItem>
          ))}
        </ChecklistGroup>
      ))}
      {error && <ChecklistError>{error}</ChecklistError>}
    </ChecklistSection>
  );
};

const VettingChecklistHistory = ({ application }: { application: PblLawyerProfile }) => {
  //   @ts-ignore
  const { t } = useTranslation();
  const rounds = getPblVettingChecklistRounds(application);
  const pblVettingChecklistGroups = useMemo(() => getPblVettingChecklistGroups(t), [t]);
  if (rounds.length === 0) return null;

  return (
    <ChecklistHistorySection>
      <SectionTitle>{t('pbl.pagesHardcoded.vettingDetail.vettingChecklistHistory')}</SectionTitle>
      {rounds.map((round) => (
        <ChecklistHistoryRound key={round.id}>
          <ChecklistHistoryTitle>{getChecklistHistoryTitle(round)}</ChecklistHistoryTitle>
          <ChecklistHistoryList>
            {pblVettingChecklistGroups.flatMap((group) =>
              group.items.map((item) => {
                const completed = round.vetting_checklist
                  ? getChecklistItemValue(round.vetting_checklist, group.key, item.key)
                  : false;

                return (
                  <ChecklistHistoryItem key={`${round.id}-${group.key}-${item.key}`}>
                    <span>{item.label}</span>
                    <ChecklistStatus completed={completed}>{completed ? t('pbl.pagesHardcoded.vettingDetail.completed') : t('pbl.pagesHardcoded.vettingDetail.missing')}</ChecklistStatus>
                  </ChecklistHistoryItem>
                );
              })
            )}
          </ChecklistHistoryList>
        </ChecklistHistoryRound>
      ))}
    </ChecklistHistorySection>
  );
};

const ApplicationInfoModal = ({
  application,
  open,
  onClose,
}: {
  application: PblLawyerProfile;
  open: boolean;
  onClose: () => void;
}) => {
  //   @ts-ignore
  const { t } = useTranslation();
  const summary = useMemo(() => buildReviewSummaryFromProfile(application), [application]);
  const firmAuthorisationDocument = useMemo(() => getPblVettingFirmAuthorisationDocument(application), [application]);
  const conductSupportingDocuments = useMemo(() => getPblVettingConductSupportingDocuments(application), [application]);

  return (
    <Modal open={open} onClose={onClose} disableEnforceFocus>
      <ModalCard wide>
        <CloseButton type="button" onClick={onClose} aria-label={t('pbl.pagesHardcoded.vettingDetail.close')}>
          x
        </CloseButton>
        <InfoModalScrollArea>
          <ReviewSummaryContent
            form={summary.form}
            serviceAreaLabels={summary.serviceAreaLabels}
            showHeader={false}
            showEditLinks={false}
            showSubmitNotice={false}
            firmAuthorisationDocument={firmAuthorisationDocument}
            firmAuthorisationFileName={application.firm_authorisation_file_name}
            conductSupportingDocuments={conductSupportingDocuments}
            conductSupportingDocumentNames={application.conduct_supporting_document_names}
          />
        </InfoModalScrollArea>
      </ModalCard>
    </Modal>
  );
};

const ReviewDecisionModal = ({
  type,
  decisionReason,
  reviewerComments,
  onClose,
  onConfirm,
  isSubmitting,
}: {
  type: ReviewModalType | null;
  decisionReason: string;
  reviewerComments: string;
  onClose: () => void;
  onConfirm: () => void;
  isSubmitting: boolean;
}) => {
  //   @ts-ignore
  const { t } = useTranslation();
  return (
  <Modal open={Boolean(type)} onClose={onClose} disableEnforceFocus>
    <ModalCard>
      {type === 'approve' ? (
        <>
          <ConfirmText>{t('pbl.pagesHardcoded.vettingDetail.thisLawyerWillBecomeActiveAndEligibleFor')}</ConfirmText>
          <ModalActions>
            <ModalButton type="button" variant="primary" onClick={onConfirm} disabled={isSubmitting}>
              {t('pbl.pagesHardcoded.vettingDetail.confirmApproval')}</ModalButton>
            <ModalButton type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
              {t('pbl.pagesHardcoded.vettingDetail.cancel')}</ModalButton>
          </ModalActions>
        </>
      ) : (
        <>
          <ConfirmText>
            {t('pbl.pagesHardcoded.vettingDetail.youWillRequestResubmissionOfThisLawyerS')}<PendingReason>
              {t('pbl.pagesHardcoded.vettingDetail.reasonForResubmission')}<br />
              {decisionReason || '-'}
            </PendingReason>
            <PendingReason>
              {t('pbl.pagesHardcoded.vettingDetail.reviewerComments')}<br />
              {reviewerComments || '-'}
            </PendingReason>
          </ConfirmText>
          <ModalActions>
            <ModalButton type="button" variant="primary" onClick={onConfirm} disabled={isSubmitting}>
              {t('pbl.pagesHardcoded.vettingDetail.sendRequest')}</ModalButton>
            <ModalButton type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
              {t('pbl.pagesHardcoded.vettingDetail.cancel')}</ModalButton>
          </ModalActions>
        </>
      )}
    </ModalCard>
  </Modal>
);
};

const PblVettingDetail = () => {
  //   @ts-ignore
  const { t } = useTranslation();
  const { profileId } = useParams();
  const navigate = useNavigate();
  const { isMobile } = useResize();
  const [application, setApplication] = useState<PblLawyerProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const summary = useMemo(() => (application ? buildReviewSummaryFromProfile(application) : null), [application]);
  const [decisionReason, setDecisionReason] = useState('');
  const [returnReasonOther, setReturnReasonOther] = useState('');
  const [comments, setComments] = useState('');
  const [vettingChecklist, setVettingChecklist] = useState<PblVettingChecklist>(() => createEmptyPblVettingChecklist());
  const [checklistError, setChecklistError] = useState('');
  const [infoOpen, setInfoOpen] = useState(false);
  const [reviewModal, setReviewModal] = useState<ReviewModalType | null>(null);
  const resubmitReasons = application ? getPblVettingResubmitReasons(application) : [];
  const showReviewActions = application ? shouldShowPblVettingReviewActions(application) : false;

  useEffect(() => {
    let isMounted = true;
    const numericProfileId = Number(profileId);

    const fetchApplication = async () => {
      if (!numericProfileId) {
        setIsLoading(false);
        setErrorMessage('Invalid attorney application.');
        return;
      }

      setIsLoading(true);
      setErrorMessage('');

      try {
        const response = await getPblVettingApplication(numericProfileId);

        if (!isMounted) return;

        if (!response.data.success) {
          const message = response.data.message || 'Failed to load attorney application.';
          setApplication(null);
          setErrorMessage(message);
          errorHandler(message);
          return;
        }

        setApplication(response.data.data);
        setComments(response.data.data.reviewer_comments || '');
        setVettingChecklist(createEmptyPblVettingChecklist());
        setChecklistError('');
      } catch (error: any) {
        if (!isMounted) return;

        const message =
          error.response?.data?.message ||
          error.response?.data?.data ||
          error.message ||
          'Failed to load attorney application.';
        setApplication(null);
        setErrorMessage(message);
        errorHandler(message);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    fetchApplication();

    return () => {
      isMounted = false;
    };
  }, [profileId]);

  const updateChecklistItem = (groupKey: keyof PblVettingChecklist, itemKey: string) => {
    setVettingChecklist((current) => {
      const currentGroup = current[groupKey] as Record<string, boolean>;

      return {
        ...current,
        [groupKey]: {
          ...currentGroup,
          [itemKey]: !currentGroup[itemKey],
        },
      } as PblVettingChecklist;
    });
    setChecklistError('');
  };

  const openReviewModal = (type: ReviewModalType) => {
    if (type === 'approve' && !isPblVettingChecklistComplete(vettingChecklist)) {
      setChecklistError(pblVettingChecklistErrorMessage);
      errorHandler(pblVettingChecklistErrorMessage);
      return;
    }

    if (type === 'resubmit') {
      const visibleReason = decisionReason === 'Other' ? returnReasonOther : decisionReason;

      if (!visibleReason.trim()) {
        errorHandler(t('pbl.pagesHardcoded.vettingDetail.pleaseProvideAReturnReason'));
        return;
      }
    }

    setReviewModal(type);
  };

  const confirmReview = async () => {
    if (!reviewModal || !application) return;

    const isOtherReason = decisionReason === 'Other';
    const visibleReason = isOtherReason ? returnReasonOther : decisionReason;

    if (reviewModal === 'resubmit' && !visibleReason.trim()) {
      errorHandler(t('pbl.pagesHardcoded.vettingDetail.pleaseProvideAReturnReason'));
      return;
    }

    if (reviewModal === 'approve' && !isPblVettingChecklistComplete(vettingChecklist)) {
      setChecklistError(pblVettingChecklistErrorMessage);
      errorHandler(pblVettingChecklistErrorMessage);
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = buildPblVettingReviewPayload(
        reviewModal,
        {
          returnReasonCode: isOtherReason ? 'other' : decisionReason,
          returnReason: decisionReason,
          returnReasonOther: isOtherReason ? returnReasonOther : undefined,
          reviewerComments: comments,
        },
        reviewModal === 'approve' ? vettingChecklist : undefined
      );
      const response = await reviewPblLawyerApplication(application.id, payload);

      if (!response.data.success) {
        errorHandler(response.data.message || t('pbl.pagesHardcoded.vettingDetail.failedToSubmitAttorneyReview'));
        return;
      }

      const updatedApplication = response.data.data;
      setApplication(updatedApplication);
      setComments(updatedApplication.reviewer_comments || '');
      setReviewModal(null);
      successHandler(t('pbl.pagesHardcoded.vettingDetail.applicationMarkedAsValue', { value1: pblVettingStatusLabels[getPblVettingProfileStatus(updatedApplication)] }));
    } catch (error: any) {
      errorHandler(
        error.response?.data?.message ||
          error.response?.data?.data ||
          error.message ||
          t('pbl.pagesHardcoded.vettingDetail.failedToSubmitAttorneyReview')
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <PageContainer title={t('pbl.pagesHardcoded.vettingDetail.reviewOfProbonoLawyersApplication')}>
        <Loading />
      </PageContainer>
    );
  }

  if (!application || !summary) {
    return (
      <PageContainer
        title={
          <TitleBar isMobile={isMobile}>
            <Button size="md" variant="primary" rounded={false} onClick={() => navigate(PATH.pbl.vettingApplications)}>
              {t('pbl.pagesHardcoded.vettingDetail.back')}</Button>
            <span>
              {t('pbl.pagesHardcoded.vettingDetail.home')}<strong>{t('pbl.pagesHardcoded.vettingDetail.reviewOfProbonoLawyersApplication')}</strong>
            </span>
          </TitleBar>
        }
      >
        <StateMessage>{errorMessage || t('pbl.pagesHardcoded.vettingDetail.attorneyApplicationNotFound')}</StateMessage>
      </PageContainer>
    );
  }

  return (
    <PageContainer
      title={
        <TitleBar isMobile={isMobile}>
          <Button size="md" variant="primary" rounded={false} onClick={() => navigate(PATH.pbl.vettingApplications)}>
            {t('pbl.pagesHardcoded.vettingDetail.back')}</Button>
          <span>
            {t('pbl.pagesHardcoded.vettingDetail.homeReviewOfProbonoLawyersApplication')}<strong>{application.full_name_en}</strong>
          </span>
        </TitleBar>
      }
    >
      <ReviewCard>
        <LawyerSummaryCard>
          <LawyerName>{application.full_name_en}</LawyerName>
          <SummaryRows>
            <Label>{t('pbl.pagesHardcoded.vettingDetail.applicationReference')}</Label>
            <Value>{getPblVettingProfileReference(application)}</Value>
            <Label>{t('pbl.pagesHardcoded.vettingDetail.submittedDate')}</Label>
            <Value>{getPblVettingProfileCreatedAt(application)}</Value>
            <Label>{t('pbl.pagesHardcoded.vettingDetail.status')}</Label>
            <Value>{pblVettingStatusLabels[getPblVettingProfileStatus(application)]}</Value>
            <Label>{t('pbl.pagesHardcoded.vettingDetail.professionalQualificationCertifications')}</Label>
            <Value>{summary.form.cv_other_qualifications_en || '-'}</Value>
            <Label>{t('pbl.pagesHardcoded.vettingDetail.admittedYears')}</Label>
            <Value>{getAdmittedYears(application)}</Value>
            <Label>{t('pbl.pagesHardcoded.vettingDetail.languages')}</Label>
            <Value>{summary.form.cv_languages_en || '-'}</Value>
            <Label>{t('pbl.pagesHardcoded.vettingDetail.primaryPracticeArea')}</Label>
            <Value>{summary.form.cv_primary_practice_areas_en || summary.serviceAreaLabels.join(', ') || '-'}</Value>
            <Label>{t('pbl.pagesHardcoded.vettingDetail.completedProBonoCaseS')}</Label>
            <Value>{getPblVettingCompletedProBonoCaseCount(application)}</Value>
          </SummaryRows>
          <Divider />
          <SectionTitle style={{ margin: '0 0 12px' }}>{t('pbl.pagesHardcoded.vettingDetail.cvOfTheProbonoLawyer')}</SectionTitle>
          <CvButton type="button" onClick={() => setInfoOpen(true)}>
            {t('pbl.pagesHardcoded.vettingDetail.details')}</CvButton>
        </LawyerSummaryCard>

        {resubmitReasons.length > 0 && (
          <>
            <SectionTitle>{t('pbl.pagesHardcoded.vettingDetail.reasonForNeedingToReSubmit')}</SectionTitle>
            <ResubmitTable>
              <thead>
                <tr>
                  <th>{t('pbl.pagesHardcoded.vettingDetail.reason')}</th>
                  <th>{t('pbl.pagesHardcoded.vettingDetail.date')}</th>
                </tr>
              </thead>
              <tbody>
                {resubmitReasons.map((item) => (
                  <tr key={`${item.reason}-${item.date}`}>
                    <td data-label={t('pbl.pagesHardcoded.vettingDetail.reason')}>{item.reason}</td>
                    <td data-label={t('pbl.pagesHardcoded.vettingDetail.date')}>{item.date}</td>
                  </tr>
                ))}
              </tbody>
            </ResubmitTable>
          </>
        )}

        <SectionTitle>{t('pbl.pagesHardcoded.vettingDetail.vettingRemarks')}</SectionTitle>
        <FormArea>
          <FormControl fullWidth variant="standard">
            <Select
              value={decisionReason}
              displayEmpty
              onChange={(event) => setDecisionReason(event.target.value)}
              renderValue={(selected) => (selected ? selected : t('pbl.pagesHardcoded.vettingDetail.decisionReason'))}
            >
              {pblDecisionReasons.map((reason) => (
                <MenuItem key={reason} value={reason}>
                  {reason}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {decisionReason === t('pbl.pagesHardcoded.vettingDetail.other') && (
            <TextField
              label={t('pbl.pagesHardcoded.vettingDetail.otherReason')}
              variant="standard"
              value={returnReasonOther}
              onChange={(event) => setReturnReasonOther(event.target.value)}
            />
          )}
          <TextField
            label={t('pbl.pagesHardcoded.vettingDetail.reviewerComments2')}
            variant="standard"
            value={comments}
            multiline
            minRows={2}
            onChange={(event) => setComments(event.target.value)}
            placeholder={t('pbl.pagesHardcoded.vettingDetail.pleaseLeaveACommentToTheLawyerAbout')}
          />
        </FormArea>

        <SectionTitle>{t('pbl.pagesHardcoded.vettingDetail.vettingChecklist')}</SectionTitle>
        <VettingChecklistFields checklist={vettingChecklist} error={checklistError} onToggle={updateChecklistItem} />
        <VettingChecklistHistory application={application} />

        {showReviewActions && (
          <ActionRow isMobile={isMobile}>
            <ActionButton
              tone="approve"
              type="button"
              onClick={() => openReviewModal('approve')}
              disabled={isSubmitting}
            >
              {t('pbl.pagesHardcoded.vettingDetail.approveApplication')}</ActionButton>
            <ActionButton
              tone="pending"
              type="button"
              onClick={() => openReviewModal('resubmit')}
              disabled={isSubmitting}
            >
              {t('pbl.pagesHardcoded.vettingDetail.requestForResubmission')}</ActionButton>
          </ActionRow>
        )}
      </ReviewCard>

      <ApplicationInfoModal application={application} open={infoOpen} onClose={() => setInfoOpen(false)} />
      <ReviewDecisionModal
        type={reviewModal}
        decisionReason={decisionReason === t('pbl.pagesHardcoded.vettingDetail.other') ? returnReasonOther : decisionReason}
        reviewerComments={comments}
        onClose={() => setReviewModal(null)}
        onConfirm={confirmReview}
        isSubmitting={isSubmitting}
      />
    </PageContainer>
  );
};

export default PblVettingDetail;
